import * as React from 'react';
import { useEffect, useState } from 'react';

import { compose } from 'recompose';
import _ from 'underscore';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import keysToConstants from 'js/lib/keysToConstants';
import { stringKeyToTuple } from 'js/lib/stringKeyTuple';

import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { FormattedCourse } from 'bundles/enroll-common/types/enrollSectionTypes';
import type { ApiError } from 'bundles/enroll-common/utils/errorUtils';
import { trapFocusInModal } from 'bundles/enroll-common/utils/trapFocusInModal';
import EnrollErrorModal from 'bundles/enroll-modal/components/common/EnrollErrorModal';
import PromotionApplicableCheckoutMessage from 'bundles/enroll-modal/components/common/PromotionApplicableCheckoutMessage';
import CredentialTrackSubscriptionEnrollButton from 'bundles/enroll-modal/components/credential-track/CredentialTrackSubscriptionEnrollButton';
import type PromotionEligibilitiesV1 from 'bundles/naptimejs/resources/promotionEligibilities.v1';
import ReactPriceDisplay from 'bundles/ownable-products-common/components/ReactPriceDisplay';
import type {
  OwnableProduct,
  PropsFromWithCredentialTrackOwnableProducts,
} from 'bundles/ownable-products-common/components/xdp/withNaptimeData';
import { useGetProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';
import type { ProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';
import ProductTypeObject from 'bundles/payments-common/common/ProductType';
import type { ProductType } from 'bundles/payments-common/common/ProductType';
import Modal from 'bundles/phoenix/components/Modal';
import type { Maybe } from 'bundles/premium-certificate/__generated__/baseContentfulTypes';
import withPromotionInfo from 'bundles/promotions/components/withPromotionInfo';

import _t from 'i18n!nls/enroll';

import 'css!./__styles__/CredentialTrackEnrollModal';

const MODAL_TYPES = keysToConstants(['ENROLL', 'ERROR']);

const EVENTING_V3_MODAL_NAME = 'mastertrack_purchase';

type InputProps = {
  onClose: () => void;
  courses?: Array<FormattedCourse>;
  certificateType?: Maybe<string>;
} & PropsFromWithCredentialTrackOwnableProducts;

type Props = InputProps & {
  promotionEligibilities?: PromotionEligibilitiesV1;
};

const DEFAULT_CYCLES = 1;

const CredentialTrackEnrollModal = ({
  onClose,
  ownableProducts,
  courses,
  certificateType = _t('program'),
  promotionEligibilities,
}: Props) => {
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [underlyingProductId, setUnderlyingProductId] = useState<string | undefined>();
  const [activeModal, setActiveModal] = useState<keyof typeof MODAL_TYPES | null>(MODAL_TYPES.ENROLL);
  const [error, setError] = useState<ApiError | undefined>();
  const [selectedProductType, setSelectedProductType] = useState<string | undefined>();

  const productItemIds =
    (!_(ownableProducts).isEmpty() &&
      _(ownableProducts)
        .chain()
        .map((ownableProduct) => ownableProduct.id.split('~')[1])
        .value()) ||
    [];
  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const productType = ownableProducts && (ownableProducts[0].productType as ProductType);
  const products =
    !_(productItemIds).isEmpty() && productType
      ? productItemIds.map((productItemId) => ({
          productType,
          productItemId,
        }))
      : [];

  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const { productPrices, loading } = useGetProductPrices({ products, skip: products.length === 0 });

  useEffect(() => {
    trapFocusInModal('.c-modal-content');

    if (ownableProducts) {
      _(ownableProducts).some((ownableProduct: $TSFixMe) => {
        if (!ownableProduct.numberOfCycles || ownableProduct.numberOfCycles === DEFAULT_CYCLES) {
          setSelectedProductId(ownableProduct.id);
          setUnderlyingProductId(ownableProduct.underlyingProductId as string | undefined);
          setSelectedProductType(ownableProduct.productType);
          return true;
        }
        return false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBillingTypeChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const selectedValue = e.currentTarget.value;
    const newSelectedProductType = ownableProducts?.find(({ id }) => id === selectedValue)?.productType;
    setSelectedProductId(selectedValue);
    setSelectedProductType(newSelectedProductType);
  };

  const handleError = (newError: ApiError) => {
    setActiveModal(MODAL_TYPES.ERROR);
    setError(newError);
  };

  const renderCoursePricing = (ownableProduct: OwnableProduct) => {
    const productPrice =
      productPrices &&
      productPrices.filter((price: ProductPrices) => price.productItemId === ownableProduct.id.split('~')[1])[0];

    const amount = productPrice && productPrice.finalAmount;
    const currencyCode = productPrice && productPrice.currencyCode;
    const hasPrice = typeof amount === 'number' && amount >= 0;
    const frequency =
      ownableProduct.productType === ProductTypeObject.CREDENTIAL_TRACK || ownableProduct.numberOfCycles === 1
        ? _t('up front')
        : _t('monthly');
    return (
      <div className="course-price">
        {hasPrice && (
          <FormattedMessage
            message={_t('{amount} {frequency}')}
            amount={<ReactPriceDisplay value={amount} currency={currencyCode} />}
            frequency={frequency}
          />
        )}
      </div>
    );
  };

  const renderBody = (): React.ReactNode => {
    const paymentOptionsGroupId = 'enroll-modal-payment-options-group';

    return (
      <div className="horizontal-box c-row-underline p-y-2">
        <div className="headline-1-text m-r-2 text-nowrap">
          <strong id={paymentOptionsGroupId}>{_t('Payment Options')}</strong>
        </div>
        <div className="vertical-box">
          <div className="horizontal-box" role="radiogroup" aria-labelledby={paymentOptionsGroupId}>
            {ownableProducts &&
              ownableProducts.map((ownableProduct) => {
                const { id } = ownableProduct;
                return (
                  <div key={id} className="m-r-2 horizontal-box align-items-vertical-center">
                    <input
                      id={id}
                      type="radio"
                      name="billingType"
                      value={ownableProduct.id}
                      onClick={handleBillingTypeChange}
                      defaultChecked={
                        !ownableProduct.numberOfCycles ||
                        ownableProduct.numberOfCycles === DEFAULT_CYCLES ||
                        ownableProduct.id === selectedProductId
                      }
                    />
                    <label htmlFor={id} className="body-1-text m-l-1s m-b-0">
                      <div>
                        <strong>
                          <FormattedMessage
                            message={_t('{numPayments, plural, =1 {1 Payment} other {# Payments}}')}
                            numPayments={ownableProduct.numberOfCycles ?? 1}
                          />
                        </strong>
                      </div>
                      <div className="caption-text hint-text-color">{renderCoursePricing(ownableProduct)}</div>
                    </label>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    );
  };

  const renderNumCourses = (): React.ReactNode => {
    const numCourses = courses ? courses.length : 0;
    return (
      <div className="horizontal-box c-row-underline p-y-2">
        <div className="headline-1-text m-r-1">
          <strong>{_t('Number of Courses')}</strong>
        </div>
        <div className="horizontal-box">
          <strong>
            {numCourses > 0 ? (
              <FormattedMessage
                message={_t('{numCourses} Courses - Full #{certificateType} Program', { certificateType })}
                numCourses={numCourses}
              />
            ) : (
              _t('Full #{certificateType} Program', { certificateType })
            )}
          </strong>
        </div>
      </div>
    );
  };

  const promoCode = promotionEligibilities?.isEligible ? promotionEligibilities?.promoCodeId : null;

  const [, masterTrackUnderlyingProductItemId] = (underlyingProductId && stringKeyToTuple(underlyingProductId)) || [];

  const track = useTracker();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_modal', {
    modal: {
      name: EVENTING_V3_MODAL_NAME,
    },
    product: {
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      id: masterTrackUnderlyingProductItemId,
    },
  });

  const handleClose = () => {
    track('click_button', {
      button: {
        name: 'dismiss',
      },
      modal: {
        name: EVENTING_V3_MODAL_NAME,
      },
      product: {
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        id: masterTrackUnderlyingProductItemId,
      },
    });

    onClose();
  };

  const onEnrollButtonClick = () => {
    const selectedNumberOfCycles =
      ownableProducts?.find(({ id }) => id === selectedProductId)?.numberOfCycles ?? DEFAULT_CYCLES;

    track('click_button', {
      button: {
        name: 'continue_process',
      },
      modal: {
        name: EVENTING_V3_MODAL_NAME,
      },
      enrollmentOptions: {
        enrollmentOptionsType: selectedNumberOfCycles === 1 ? 'mastertrack_upfront' : 'mastertrack_quarterly',
      },
      product: {
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        id: masterTrackUnderlyingProductItemId,
      },
    });
  };

  if (loading) {
    return null;
  }

  switch (activeModal) {
    case MODAL_TYPES.ENROLL:
      return (
        <Modal
          modalName={_t('CredentialTrack Enroll Modal')}
          className="rc-CredentialTrackEnrollModal"
          handleClose={handleClose}
        >
          <h4 className="headline-4-text color-secondary-text" ref={visibilityTrackingRef}>
            {_t('Complete Your Purchase')}
          </h4>
          {underlyingProductId && (
            <PromotionApplicableCheckoutMessage
              masterTrackUnderlyingProductItemId={masterTrackUnderlyingProductItemId}
            />
          )}
          <p className="body-container c-row-underline m-t-3 p-b-3 m-b-0">
            {_t(
              `By purchasing this #{certificateType}, you are purchasing access to all courses in the program. Your payment will grant you access to the #{certificateType} courses for the duration of the program.`,
              { certificateType }
            )}
          </p>
          {renderNumCourses()}
          {renderBody()}
          <CredentialTrackSubscriptionEnrollButton
            productType={selectedProductType}
            productId={selectedProductId}
            onError={handleError}
            onClick={onEnrollButtonClick}
            promoCode={promoCode ?? undefined}
          />
        </Modal>
      );
    case MODAL_TYPES.ERROR:
      return <EnrollErrorModal error={error} onClose={onClose} />;
    default:
      return null;
  }
};

export default compose<Props, InputProps>(withPromotionInfo())(CredentialTrackEnrollModal);
